import { type UseModalState } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";

import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import { TextButton } from "../../components/TextButton";

interface ChartingReviewFooterProps {
  chartingNoncomplianceReviewModalState: UseModalState;
  workerId: string;
  workplaceId: string;
  isSuccess: boolean;
  checkGeofenceStatus: () => void;
}

export function ChartingReviewFooter({
  chartingNoncomplianceReviewModalState,
  workerId,
  workplaceId,
  isSuccess,
  checkGeofenceStatus,
}: ChartingReviewFooterProps) {
  const history = useHistory();
  const logMetadata = {
    timestamp: new Date().toISOString(),
    workerId,
    workplaceId,
  };
  const queryClient = useQueryClient();

  const invalidateChartingNoncompliancesQuery = () => {
    // Invalidate the charting noncompliances query to refetch updated data
    void queryClient.invalidateQueries({
      queryKey: ["chartingNoncompliance", workerId],
    });
  };

  return (
    <DialogFooter orientation="vertical">
      {isSuccess ? (
        <Stack spacing={4} sx={{ width: "100%", alignItems: "center" }}>
          <Button
            size="large"
            role="button"
            sx={{ width: "100%" }}
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.CHARTING_NONCOMPLIANCE_GOT_IT, logMetadata);

              chartingNoncomplianceReviewModalState.closeModal();
              invalidateChartingNoncompliancesQuery();
            }}
          >
            Got it
          </Button>

          <TextButton
            underline
            size="large"
            sx={{ lineHeight: 1.5, padding: 4 }}
            onClick={() => {
              logEvent(
                APP_V2_USER_EVENTS.CHARTING_NONCOMPLIANCE_WORKPLACE_MESSAGED_AFTER_REVIEW,
                logMetadata
              );
              chartingNoncomplianceReviewModalState.closeModal();
              invalidateChartingNoncompliancesQuery();
              history.push(`/home/account/chat/${workplaceId}`);
            }}
          >
            Notify the workplace
          </TextButton>
        </Stack>
      ) : (
        <Stack spacing={4} sx={{ width: "100%", alignItems: "center" }}>
          <Button
            size="large"
            role="button"
            sx={{ width: "100%" }}
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.CHARTING_NONCOMPLIANCE_TRY_AGAIN, logMetadata);
              checkGeofenceStatus();
            }}
          >
            I am inside the workplace — Try again
          </Button>

          <TextButton
            underline
            size="large"
            sx={{ lineHeight: 1.5, padding: 4 }}
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.CHARTING_NONCOMPLIANCE_WORKPLACE_MESSAGED, logMetadata);
              chartingNoncomplianceReviewModalState.closeModal();
              history.push(`/home/account/chat/${workplaceId}`);
            }}
          >
            I left, notify the workplace
          </TextButton>
        </Stack>
      )}
    </DialogFooter>
  );
}
